// src/components/IdeaEnhancementModal.jsx
import React, { useState } from 'react';
import { X, Check, RotateCcw, Loader2 } from 'lucide-react';

function IdeaEnhancementModal({ isOpen, onClose, enhancement, onApply, onRegenerate, isRegenerating }) {
  const [editedTitle, setEditedTitle] = useState(enhancement?.title || '');
  const [editedDescription, setEditedDescription] = useState(enhancement?.description || '');
  const [editedTags, setEditedTags] = useState(enhancement?.tags || []);
  const [editedSubtasks, setEditedSubtasks] = useState(enhancement?.subtasks || []);
  const [newTag, setNewTag] = useState('');
  const [newSubtask, setNewSubtask] = useState('');

  // Update local state when enhancement changes
  React.useEffect(() => {
    if (enhancement) {
      setEditedTitle(enhancement.title || '');
      setEditedDescription(enhancement.description || '');
      setEditedTags(enhancement.tags || []);
      setEditedSubtasks(enhancement.subtasks || []);
    }
  }, [enhancement]);

  if (!isOpen || !enhancement) return null;

  const handleAddTag = () => {
    if (newTag.trim() && !editedTags.includes(newTag.trim())) {
      setEditedTags([...editedTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditedTags(editedTags.filter(tag => tag !== tagToRemove));
  };

  const handleAddSubtask = () => {
    if (newSubtask.trim()) {
      setEditedSubtasks([...editedSubtasks, { task: newSubtask.trim(), completed: false }]);
      setNewSubtask('');
    }
  };

  const handleRemoveSubtask = (index) => {
    setEditedSubtasks(editedSubtasks.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    onApply({
      title: editedTitle,
      description: editedDescription,
      tags: editedTags,
      subtasks: editedSubtasks,
      planSteps: enhancement.planSteps || {}
    });
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-3xl lg:max-w-6xl h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              AI Enhancement Preview
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - Two Column Layout on Desktop */}
          <div className="flex-1 overflow-hidden p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-6 h-full">
              {/* Left Column - User Inputs */}
              <div className={`flex flex-col gap-6 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:dark:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent ${(!enhancement.planSteps || Object.keys(enhancement.planSteps).length === 0) ? 'lg:col-span-2' : ''}`}>
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title
              </label>
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter idea title"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:dark:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
                placeholder="Enhanced description"
              />
            </div>

            {/* Tags */}
            <div className="flex flex-col gap-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {editedTags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:bg-primary/30 rounded-full p-0.5"
                      aria-label={`Remove ${tag} tag`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Add tag"
                />
                <button
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-md transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Subtasks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subtasks / To-dos
              </label>
              {editedSubtasks.length > 0 && (
                <div className="border border-gray-200 dark:border-white/10 rounded-md overflow-hidden mb-2 max-h-60 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:dark:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
                  <ul className="divide-y divide-gray-200 dark:divide-white/10">
                    {editedSubtasks.map((subtask, index) => {
                      const taskText = typeof subtask === 'string' ? subtask : subtask.task;
                      return (
                        <li key={index} className="flex items-center gap-2 px-3 py-2">
                          <span className="text-gray-700 dark:text-gray-300 flex-1">{taskText}</span>
                          <button
                            onClick={() => handleRemoveSubtask(index)}
                            className="text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 transition-colors flex-shrink-0"
                            aria-label={`Remove subtask ${index + 1}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask()}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Add subtask"
                />
                <button
                  onClick={handleAddSubtask}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-md transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
              </div>

              {/* Right Column - Plan Steps Preview */}
              {enhancement.planSteps && Object.keys(enhancement.planSteps).length > 0 && (
                <div className="flex flex-col h-full overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:dark:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
                  <div className="flex-1 flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Plan Steps (Preview)
                    </label>
                    <div className="bg-black/20 dark:bg-slate-950 border border-slate-700 rounded-md p-4 space-y-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-slate-500 [&::-webkit-scrollbar-thumb]:dark:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 lg:hidden">
                        ℹ️ These steps will be appended to your description.
                      </p>
                      {Object.entries(enhancement.planSteps).map(([category, steps]) => (
                        <div key={category}>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{category}</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
                            {Array.isArray(steps) && steps.map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions - Sticky */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-4">
              <button
                onClick={onRegenerate}
                disabled={isRegenerating}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    Regenerate
                  </>
                )}
              </button>
              {enhancement.planSteps && Object.keys(enhancement.planSteps).length > 0 && (
                <>
                  <div className="hidden lg:block border-r border-gray-300 dark:border-gray-600 h-6 mx-2" />
                  <p className="text-xs text-gray-500 dark:text-gray-400 hidden lg:block ml-2">
                    Plan steps will be automatically added to the description when you click "Apply Changes".
                  </p>
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-md transition-colors"
              >
                <Check className="w-4 h-4" />
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default IdeaEnhancementModal;
